import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { BookOpen, ChevronDown, ChevronRight, ShieldAlert, CheckSquare, Brain, Target, AlertTriangle, Users, ClipboardList, Megaphone, Ban, Compass, Gauge, Lightbulb, Swords } from "lucide-react";
import { apiGet, unpackList } from "../../../../Api/Client.js";
import { errorText } from "../../../../Api/errorText.js";
import { useCopyProtection } from "../../../../Shared/useCopyProtection.js";

// Playbook — goal 2 of the sales OS. A living document, built by guiding
// Ankit and Azaan, result-oriented. Reads the SalesGuide deal-stage models
// (Business -> DealStage -> Substep) that already exist and already carry
// real content (guardrails, best practices, buyer psychology per stage) —
// this page is a new, dedicated surface over that same data, not a new
// content store. RUDIMENTARY FIRST PASS: read-only. Editing happens in
// Django admin for now.

const card = { background: "var(--surface)", border: "1px solid var(--c-200)", borderRadius: 14, padding: "16px 18px" };
const muted = { color: "var(--c-500)" };

// The element that actually scrolls this page. In this app shell that is the
// content panel (`.Atg-Content`), never `window` — a mistake made twice
// already on this exact page (the outline rail's own position tracking, and
// the per-tab scroll memory below), both times because it is easy to assume
// a page scrolls the way the browser tab itself does. Found by walking up
// from a known element rather than hard-coding the class name, so a shell
// restyle cannot silently reintroduce this a third time.
function nearestScroller(el) {
  for (let node = el?.parentElement; node; node = node.parentElement) {
    if (/(auto|scroll|overlay)/.test(getComputedStyle(node).overflowY)) return node;
  }
  return null;
}

// Deal-size bands. The whole page reads differently depending on which one is
// selected — the buyer is a different person and the move is a different move.
// Same eleven stages and the same substep names at every tier. What changes is
// density, register, and how many people must nod. `floor` is the entry deal
// size, shown on the button so the band is not an unlabelled letter.
const BANDS = [
  { key: "small", short: "S", label: "Small", tier: "T1", floor: "₹40K+", size: "₹40K–₹6L",
    buyer: "Founder / owner", cycle: "2–3 calls, WhatsApp", nod: "1 person; buyer = signer", register: "Plain, direct, founder-talk",
    hover: "Founder-led. One person decides, uses and signs. They buy speed and relief, not a business case — an ROI model reads as overkill here." },
  { key: "mid", short: "M", label: "Mid", tier: "T2", floor: "₹15L+", size: "₹15–50L",
    buyer: "CTO / Head of function", cycle: "4–6 calls", nod: "2–3 people, light committee", register: "Function-aware, mid-formal",
    hover: "A function head with a real budget line, owning a number that is slipping. They need a case they can defend to their boss." },
  { key: "large", short: "L", label: "Large", tier: "T3", floor: "₹50L+", size: "₹50L–₹3Cr",
    buyer: "VP / SVP", cycle: "8–12 calls, 3–6 months", nod: "Full committee, procurement, security", register: "Formal, committee-fluent",
    hover: "Payer and user are different people, and a committee decides. Nobody gets promoted for a bold vendor choice, but people do get fired for a bad one — sell risk removal." },
  { key: "enterprise", short: "L+", label: "Enterprise", tier: "T4", floor: "₹3Cr+", size: "₹3Cr+",
    buyer: "C-suite / board", cycle: "Multi-quarter", nod: "Board narrative, MNC procurement, RFP", register: "Executive, politically fluent",
    hover: "Multi-year, multi-business-unit, procurement-heavy. You are introduced rather than discovered, and you are asking to be written into a plan that already exists." },
];

// The band above this one, for a forward pointer on a stage that does not run
// here. Returns nothing at the top band, so the pointer simply does not render.
function nextBandLabel(band) {
  const i = BANDS.findIndex((b) => b.key === band);
  return i >= 0 && i < BANDS.length - 1 ? BANDS[i + 1].label : "";
}

// Instant tooltip. Native `title` waits about a second before appearing, which
// is long enough that nobody discovers the text is there at all. Same dark box
// it always looked like — the only thing that changed is the delay.
//
// Deliberately NOT `cursor: help`: that swaps the pointer for the arrow-and-
// question-mark cursor, which is the ugly artefact and not something the rest
// of this app does anywhere.
// Measures itself, then keeps itself inside the viewport: flips below the anchor
// when there is no room above, and clamps horizontally. Without this, anything
// anchored near the top of the page — the market picker sits in the header —
// renders above the fold and is simply not there.
function TipBox({ anchor, width, children }) {
  const el = useRef(null);
  const [pos, setPos] = useState(null);
  useLayoutEffect(() => {
    const r = el.current?.getBoundingClientRect();
    if (!r) return;
    const pad = 10;
    const above = anchor.top - r.height - 9;
    const top = above >= pad ? above : Math.min(anchor.bottom + 9, window.innerHeight - r.height - pad);
    const left = Math.max(pad, Math.min(anchor.cx - r.width / 2, window.innerWidth - r.width - pad));
    setPos({ top, left });
  }, [anchor.cx, anchor.top, anchor.bottom]);

  return (
    <div
      ref={el}
      role="tooltip"
      style={{
        position: "fixed",
        left: pos ? pos.left : anchor.cx, top: pos ? pos.top : anchor.top,
        zIndex: 9999, pointerEvents: "none", maxWidth: Math.min(width, window.innerWidth - 20),
        width: "max-content",
        background: "#1f2229", color: "#f2f4f8", borderRadius: 8, padding: "9px 12px",
        fontSize: 12, lineHeight: 1.55, fontWeight: 400, textAlign: "left",
        boxShadow: "0 8px 26px rgba(0,0,0,.34)", whiteSpace: "normal",
        // Hidden for the single frame it takes to measure, so it never flashes
        // in the wrong place before settling.
        visibility: pos ? "visible" : "hidden",
      }}
    >
      {children}
    </div>
  );
}

function Hover({ content, children, width = 300, block = false }) {
  const [at, setAt] = useState(null);
  const ref = useRef(null);
  const show = () => {
    const r = ref.current?.getBoundingClientRect();
    if (r) setAt({ cx: r.left + r.width / 2, top: r.top, bottom: r.bottom });
  };
  const hide = () => setAt(null);
  if (!content) return children;
  return (
    <span
      ref={ref}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
      style={block ? { display: "inline-block" } : undefined}
    >
      {children}
      {at && createPortal(<TipBox anchor={at} width={width}>{content}</TipBox>, document.body)}
    </span>
  );
}

// The band table each playbook supplies for itself. The component knows nothing
// about any product — it renders whatever columns and rows it is handed.
//
// This used to be one hard-coded deal-value table shown on every playbook, which
// was wrong the moment a product priced per interview appeared: a junior reading
// "₹40K+" on its smallest band would anchor a conversation to a number nobody
// had agreed.
function BandTable({ labels, highlight }) {
  const cell = { padding: "4px 8px", verticalAlign: "top", whiteSpace: "nowrap" };
  const head = { ...cell, fontWeight: 700, color: "#dfe4ec", borderBottom: "1px solid #4a5261" };
  return (
    <table style={{ borderCollapse: "collapse", fontSize: 11.5 }}>
      <thead>
        <tr>{labels.columns.map((h, i) => (
          <th key={i} style={{ ...head, textAlign: "left" }}>{h}</th>
        ))}</tr>
      </thead>
      <tbody>
        {labels.rows.map((r) => {
          const on = r.band === highlight;
          return (
            <tr key={r.band} style={{ background: on ? "rgba(120,160,255,.22)" : "transparent", color: on ? "#ffffff" : "#e6eaf2" }}>
              {/* The band's own name is its identity in the row, so it reads at
                  full strength rather than being the dimmest thing in a dim table. */}
              <td style={{ ...cell, fontWeight: 800, color: on ? "#ffffff" : "#9fc0ff" }}>{r.band}</td>
              {r.cells.map((c, i) => (
                <td key={i} style={{ ...cell, fontWeight: on && i === 0 ? 700 : 400, whiteSpace: i >= 3 ? "normal" : "nowrap", maxWidth: i >= 3 ? 260 : undefined }}>{c}</td>
              ))}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function BandPicker({ value, onChange, labels }) {
  // A playbook that has not defined its own bands does not get somebody else's.
  // This used to fall back to the deal-value table, so a product with no content
  // at all still displayed four confident rupee bands above the words "no stages
  // recorded yet" — a band table is a claim, and that one was unfounded.
  if (!labels || !(labels.rows || []).length) return null;
  const L = labels;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <span style={{ fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".07em", ...muted }}>
        {L.unit_caption || "Market"}
      </span>
      <div style={{ display: "inline-flex", gap: 2, padding: 3, borderRadius: 999, background: "var(--c-100, rgba(120,120,120,.12))" }}>
        {BANDS.map((b, i) => {
          const on = value === b.key;
          const row = L.rows[i] || {};
          return (
            <Hover
              key={b.key}
              width={860}
              content={
                <>
                  <div style={{ fontWeight: 700, marginBottom: 5 }}>{row.band || b.label} — {b.hover}</div>
                  <BandTable labels={L} highlight={row.band || b.label} />
                  {(L.footnote || L.note) && (
                    <div style={{ marginTop: 7, paddingTop: 6, borderTop: "1px solid #39404d", color: "#c9d1de", fontStyle: "italic" }}>
                      {L.footnote || L.note}
                    </div>
                  )}
                </>
              }
            >
              <button
                onClick={() => onChange(b.key)}
                aria-pressed={on}
                style={{
                  display: "flex", alignItems: "baseline", gap: 5,
                  padding: "5px 11px", borderRadius: 999, border: "none", cursor: "pointer",
                  fontSize: 12, fontWeight: 700, letterSpacing: ".02em",
                  background: on ? "var(--blue-600)" : "transparent",
                  color: on ? "#fff" : "var(--c-600)",
                }}
              >
                {row.short || b.short}
                <span style={{ fontSize: 10.5, fontWeight: 600, opacity: on ? 0.85 : 0.7 }}>{row.floor || b.floor}</span>
              </button>
            </Hover>
          );
        })}
      </div>
    </div>
  );
}

// The band-specific paragraph for a stage, falling back down the ladder rather
// than showing nothing: Enterprise borrows Large where it genuinely reads the
// same, which is most stages.
function bandText(stage, band) {
  const chain = { small: ["small"], mid: ["mid", "small"], large: ["large", "mid"], enterprise: ["enterprise", "large", "mid"] };
  for (const k of chain[band] || []) {
    const v = stage[`buyer_reality_${k}`];
    if (v && v.trim()) return { text: v, from: k };
  }
  return null;
}
function substepBandText(step, band) {
  const chain = { small: ["small"], mid: ["mid", "small"], large: ["large", "mid"], enterprise: ["enterprise", "large", "mid"] };
  for (const k of chain[band] || []) {
    const v = step[`deal_size_overlay_${k}`];
    if (v && v.trim()) return { text: v, from: k };
  }
  return null;
}

// ── Vocabulary lock ─────────────────────────────────────────────────────────
// Everyone has to use these words to mean exactly one thing; ambiguity here is
// what produces useless pipeline reviews. The FIRST time a term appears in the
// document it gets a dotted underline and defines itself on hover. Only the
// first — marking every occurrence would turn the page into a minefield of
// dotted lines and stop signalling anything.

const GlossaryContext = React.createContext({ plan: {}, defs: {} });

// Superusers get every collapsible section open on load, all the way down to
// individual steps. Collapsed content is not in the DOM, so "Save as" captures
// a page of headings — this is what makes a single-shot MHTML of the whole
// playbook possible.
//
// Interim, and only for superusers. Everyone else keeps the collapsed default,
// which is the right reading experience and stays the end state once the
// content is settled.
const ExpandAllContext = React.createContext(false);

// ── Remembering where a reader was ───────────────────────────────────────────
// Which tab, which band, which sections were open — down to an individual step
// like 3.1 Context Mapping — and roughly how far down the page they had
// scrolled. So the next visit does not start over from a blank, collapsed page.
//
// localStorage rather than a cookie: this is read and written entirely by the
// browser, never needs to travel to the server on every request the way a
// cookie would, and the API has no use for it. Keyed per logged-in person, so
// a shared machine — or a superuser viewing the app as somebody else — does
// not mix up whose reading position is whose.
const UI_STORE_PREFIX = "playbook-ui:v1:";

function computeUserKey(data) {
  const u = data?.me?.user;
  return u?.username || u?.id || null;
}

function uiStorageKey(userKey) {
  return `${UI_STORE_PREFIX}${userKey || "shared"}`;
}

function loadUiState(userKey) {
  try {
    const raw = window.localStorage.getItem(uiStorageKey(userKey));
    return raw ? JSON.parse(raw) : {};
  } catch {
    // Private browsing, a full quota, storage disabled by policy — the page
    // still works, it simply will not remember anything between visits.
    return {};
  }
}

// Read-modify-write against whatever is already stored, so a scroll-position
// update does not clobber a band choice saved a moment earlier and vice versa.
function patchUiState(userKey, patch) {
  try {
    const key = uiStorageKey(userKey);
    const current = JSON.parse(window.localStorage.getItem(key) || "{}");
    window.localStorage.setItem(key, JSON.stringify({ ...current, ...patch }));
  } catch {
    // Same as above — a reader who cannot persist state can still read the page.
  }
}

// Every open/closed toggle on the page — brief chips, stages, individual steps
// — reads and writes through one flat map, keyed by a string built from a
// stable database id. Provided once at the top of the tree so a leaf component
// three levels down (a step inside a stage inside a band) does not need the map
// threaded through every intermediate prop list.
const PlaybookUIContext = React.createContext({ openMap: {}, setOpenFor: () => {} });

// `key` identifies one collapsible thing on the page — `stage:142`, `step:998`,
// `universal:icp`. Once a reader has touched it, their choice is remembered and
// wins over everything else, including expand-all. Until they have, a superuser
// sees it open (expand-all, for the MHTML capture this page is built to
// support) and everyone else sees it closed, which is the ordinary reading
// default.
function useOpenState(key) {
  const expandAll = React.useContext(ExpandAllContext);
  const { openMap, setOpenFor } = React.useContext(PlaybookUIContext);
  const remembered = key ? openMap[key] : undefined;
  const open = remembered !== undefined ? remembered : expandAll;
  const setOpen = (updater) => {
    const next = typeof updater === "function" ? updater(open) : updater;
    if (key) setOpenFor(key, next);
  };
  return [open, setOpen];
}

const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// Several of these words are ordinary English as well as terms of art — "move
// this forward", "gate" — and underlining a casual verb to define it as a noun
// would teach the wrong thing. Skipping matches that follow an auxiliary catches
// the common verb usages cheaply.
const VERBISH_BEFORE = /(?:\bto|\bcan|\bwill|\bmust|\bcannot|\bnever|\bnot|\bthey|\bwe|\byou)\s+$/i;

// A term used as a modifier is not the term. "Stall point" means the place the
// process stopped, not "no forward commitment with a date and an owner" — and
// hovering the second definition over the first actively teaches the wrong
// sense of a word the glossary exists to lock down.
const MODIFIED_AFTER = /^\s+(?:point|points|criteria|criterion|quality|check|checks|review|reviews|rate|rates|list|lists|map|maps|stage|stages|process|value)\b/i;

function findTerm(text, term) {
  const re = new RegExp(`\\b${escapeRe(term)}\\b`, "gi");
  let m;
  while ((m = re.exec(text)) !== null) {
    const before = text.slice(0, m.index);
    const after = text.slice(m.index + term.length);
    if (!VERBISH_BEFORE.test(before) && !MODIFIED_AFTER.test(after)) return m.index;
  }
  return -1;
}

// Walks every rendered string in display order and decides which node owns the
// first appearance of each term. Precomputed rather than tracked during render,
// so it does not depend on the order React happens to call components in.
function buildGlossaryPlan(current, band) {
  const glossary = current?.brief?.glossary || [];
  const defs = {};
  glossary.forEach((g) => { defs[g.term.toLowerCase()] = g.definition; });
  const pending = new Set(glossary.map((g) => g.term.toLowerCase()));
  const plan = {};
  const visit = (path, text) => {
    if (!text || !pending.size) return;
    for (const term of Array.from(pending)) {
      if (findTerm(String(text), term) >= 0) {
        pending.delete(term);
        (plan[path] ||= []).push(term);
      }
    }
  };
  const b = current?.brief;
  if (b) {
    ["icp", "pain", "worldview", "pitch"].forEach((k) => {
      visit(`brief.${k}_headline`, b[`${k}_headline`]);
      if (k === "pitch") visit("brief.pitch_one_liner", b.pitch_one_liner);
      visit(`brief.${k}_body`, b[`${k}_body`]);
      (b[`${k}_bullets`] || []).forEach((t, i) => visit(`brief.${k}_bullets.${i}`, t));
      if (k === "icp") (b.icp_disqualifiers || []).forEach((t, i) => visit(`brief.icp_disqualifiers.${i}`, t));
      if (k === "pitch") visit("brief.pitch_trap", b.pitch_trap);
    });
    (b.laws || []).forEach((l, i) => visit(`laws.${i}`, `${l.name}. ${l.body}`));
    const phys = (b.band_physics || {})[band];
    if (phys) (phys.bullets || []).forEach((t, i) => visit(`physics.${i}`, t));
  }
  (current?.stages || []).slice().sort((x, y) => x.stage_number - y.stage_number).forEach((s) => {
    ["subtitle", "definition", "must_win_mindset", "hidden_failure", "icp_context", "todo"].forEach((f) =>
      visit(`stage.${s.id}.${f}`, s[f]));
    // Band content is what actually renders, so it is what gets walked. Walking
    // the stage-level lists instead would assign a term to a bullet the reader
    // never sees, and the underline would simply never appear.
    const bc = (s.band_content || {})[band] || {};
    visit(`stage.${s.id}.band.posture`, bc.posture);
    visit(`stage.${s.id}.band.stage_note`, bc.stage_note);
    ["guardrails", "best_practices", "checklist_items"].forEach((f) =>
      ((bc[f] !== undefined ? bc[f] : s[f]) || []).forEach((t, i) => visit(`stage.${s.id}.${f}.${i}`, t)));
    if (bc.worked_example) visit(`stage.${s.id}.band.worked_example`, bc.worked_example.body);
  });
  return { plan, defs };
}

// Renders text, wrapping any term this node owns.
function Glossed({ path, children }) {
  const { plan, defs } = React.useContext(GlossaryContext);
  const text = typeof children === "string" ? children : null;
  const terms = (path && plan[path]) || [];
  if (!text || !terms.length) return children ?? null;

  const hits = terms
    .map((t) => ({ term: t, at: findTerm(text, t) }))
    .filter((h) => h.at >= 0)
    .sort((a, b) => a.at - b.at);
  const out = [];
  let cursor = 0;
  hits.forEach((h, i) => {
    if (h.at < cursor) return;
    out.push(text.slice(cursor, h.at));
    const raw = text.substr(h.at, h.term.length);
    out.push(
      <Hover key={`${h.term}-${i}`} content={<><strong>{raw}</strong> — {defs[h.term]}</>} width={330}>
        {/* Dotted, not solid: a hint that something is there, without reading
            as a link. No cursor change — the pointer stays a pointer. */}
        <span style={{ borderBottom: "1px dotted var(--c-500)", paddingBottom: 1 }}>{raw}</span>
      </Hover>,
    );
    cursor = h.at + h.term.length;
  });
  out.push(text.slice(cursor));
  return <>{out}</>;
}

function Bullets({ items, icon, color, pathPrefix }) {
  if (!items || !items.length) return null;
  return (
    <ul style={{ margin: "6px 0 0", paddingLeft: 4, listStyle: "none" }}>
      {items.map((item, i) => (
        <li key={i} style={{ display: "flex", gap: 8, alignItems: "flex-start", fontSize: 12.5, lineHeight: 1.6, padding: "3px 0", ...muted }}>
          <span style={{ marginTop: 2, flexShrink: 0 }}>{icon(color)}</span>
          <span><Glossed path={pathPrefix ? `${pathPrefix}.${i}` : undefined}>{item}</Glossed></span>
        </li>
      ))}
    </ul>
  );
}

function Substep({ step, band }) {
  const [open, setOpen] = useOpenState(`step:${step.id}`);
  return (
    <div style={{ borderTop: "1px solid var(--c-200)", padding: "10px 0" }}>
      <button
        onClick={() => setOpen((v) => !v)}
        style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", background: "transparent", border: "none", cursor: "pointer", textAlign: "left", padding: 0 }}
      >
        {open ? <ChevronDown size={14} color="var(--c-500)" /> : <ChevronRight size={14} color="var(--c-500)" />}
        <span style={{ fontSize: 13, fontWeight: 600, color: "var(--c-900)" }}>{step.number} {step.title}</span>
        {step.is_critical && (
          <span style={{ fontSize: 10, fontWeight: 700, padding: "1px 7px", borderRadius: 999, background: "var(--red-50, rgba(220,60,60,.12))", color: "var(--red-600, #c0392b)" }}>
            CRITICAL
          </span>
        )}
      </button>
      {open && (
        <div style={{ marginLeft: 22, marginTop: 6, display: "flex", flexDirection: "column", gap: 8 }}>
          {step.subtitle && <p style={{ margin: 0, fontSize: 12.5, ...muted }}>{step.subtitle}</p>}
          {/* The cells the step tree fills. Definition, thinking and example
              were on the model and had never been rendered — seeding the tree
              into fields nothing displayed would have shipped invisible work. */}
          {step.definition && (
            <div>
              <strong style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".04em", color: "var(--c-600)" }}>What you build</strong>
              <p style={{ margin: "3px 0 0", fontSize: 12.5, lineHeight: 1.6, ...muted }}>{step.definition}</p>
            </div>
          )}
          {step.thinking && (
            <div>
              <strong style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".04em", color: "var(--c-600)" }}>What you're asking yourself</strong>
              <p style={{ margin: "3px 0 0", fontSize: 12.5, lineHeight: 1.6, ...muted }}>{step.thinking}</p>
            </div>
          )}
          {step.psychology && (
            <div>
              <strong style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".04em", color: "var(--c-600)" }}>Buyer psychology</strong>
              <p style={{ margin: "3px 0 0", fontSize: 12.5, lineHeight: 1.6, ...muted }}>{step.psychology}</p>
            </div>
          )}
          {step.best_practice && (
            <div>
              <strong style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".04em", color: "var(--green-600)" }}>The move</strong>
              <p style={{ margin: "3px 0 0", fontSize: 12.5, lineHeight: 1.6, ...muted }}>{step.best_practice}</p>
            </div>
          )}
          {step.example && (
            <div>
              <strong style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".04em", color: "var(--c-600)" }}>Example</strong>
              <p style={{ margin: "3px 0 0", fontSize: 12.5, lineHeight: 1.6, ...muted }}>{step.example}</p>
            </div>
          )}
          {step.signal && (
            <div>
              <strong style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".04em", color: "var(--c-600)" }}>Signal</strong>
              <p style={{ margin: "3px 0 0", fontSize: 12.5, lineHeight: 1.6, ...muted }}>{step.signal}</p>
            </div>
          )}
          {step.deal_breaker && (
            <div>
              <strong style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".04em", color: "var(--red-600, #c0392b)" }}>Deal breaker</strong>
              <p style={{ margin: "3px 0 0", fontSize: 12.5, lineHeight: 1.6, ...muted }}>{step.deal_breaker}</p>
            </div>
          )}
          {step.gate && (
            <div>
              <strong style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".04em", color: "var(--green-600)" }}>Gate</strong>
              <p style={{ margin: "3px 0 0", fontSize: 12.5, lineHeight: 1.6, ...muted }}>{step.gate}</p>
            </div>
          )}
          {(() => {
            const ov = substepBandText(step, band);
            if (!ov) return null;
            const meta = BANDS.find((b) => b.key === band);
            return (
              <div style={{ background: "var(--blue-50, rgba(80,120,255,.08))", borderRadius: 8, padding: "9px 11px" }}>
                <strong style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".04em", color: "var(--blue-600)" }}>
                  At {meta?.label}
                  {ov.from !== band && <span style={{ ...muted, fontWeight: 500, textTransform: "none", letterSpacing: 0 }}> · as {BANDS.find((b) => b.key === ov.from)?.label}</span>}
                </strong>
                <p style={{ margin: "3px 0 0", fontSize: 12.5, lineHeight: 1.6, ...muted }}>{ov.text}</p>
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
}

// Bodies are authored with blank lines between paragraphs; rendering them raw
// collapses the whole thing into one wall of text.
function Para({ text, size = 13, path }) {
  if (!text) return null;
  const paras = String(text).split("\n\n");
  return (
    <>
      {paras.map((p, i) => (
        <p key={i} style={{ margin: i ? "9px 0 0" : 0, fontSize: size, lineHeight: 1.7, color: "var(--c-800)" }}>
          {/* Only the first paragraph is glossed — the plan keyed this whole
              field to one node, and re-scanning each paragraph would mark the
              same term more than once. */}
          {i === 0 ? <Glossed path={path}>{p}</Glossed> : p}
        </p>
      ))}
    </>
  );
}

// A section heading that carries the point on its own — he reads headings, so a
// heading that only names a category is a heading that says nothing.
function SectionHeading({ title, note }) {
  return (
    <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap", marginTop: 8, paddingTop: 14, borderTop: "1px solid var(--c-200)" }}>
      <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "var(--c-900)" }}>{title}</h2>
      <span style={{ fontSize: 12.5, ...muted }}>{note}</span>
    </div>
  );
}

// Collapsed by default, like the stages. The headline stays visible while
// collapsed, so the whole section can be read at a glance without opening
// anything — the point is legible before the detail is.
function Collapsible({ eyebrow, Icon, accent, headline, children, stateKey, navId, navLabel }) {
  const [open, setOpen] = useOpenState(stateKey);
  return (
    <div
      id={navId}
      // Leaves room for the sticky tab/band bar when a reader jumps here from
      // the outline rail, so the section lands clear of it rather than tucked
      // underneath.
      style={{ ...card, ...(navId ? { scrollMarginTop: 74 } : null) }}
      {...(navId ? { "data-nav-label": navLabel || eyebrow } : null)}
    >
      <button
        onClick={() => setOpen((v) => !v)}
        style={{ display: "flex", alignItems: "flex-start", gap: 10, width: "100%", background: "transparent", border: "none", cursor: "pointer", textAlign: "left", padding: 0 }}
      >
        <Icon size={15} color={accent} style={{ marginTop: 2, flexShrink: 0 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".06em", color: accent, marginBottom: 3 }}>{eyebrow}</div>
          <div style={{ fontSize: 15, lineHeight: 1.45, fontWeight: 700, color: "var(--c-900)" }}>{headline}</div>
        </div>
        {open ? <ChevronDown size={16} color="var(--c-500)" style={{ marginTop: 4 }} /> : <ChevronRight size={16} color="var(--c-500)" style={{ marginTop: 4 }} />}
      </button>
      {open && <div style={{ marginTop: 11 }}>{children}</div>}
    </div>
  );
}

// The brief — what a seller holds in their head BEFORE stage 0.
//
// Ordered the way a seller actually needs it: who this is for, what hurts, how
// that person already sees the world, and only then what to say. The eleven
// stages describe how a deal gains momentum and assume every one of these is
// already known; opening at Pre-Contact without them is running a sequence
// without knowing what is being sold, or to whom.
// Eyebrows carry the term of art, not a plain-English category. "Who it is for"
// names a box; "ICP · who qualifies, and who you disqualify" names the thing the
// reader has to be able to do — and teaches them the word their market uses for
// it while they are at it.
const BRIEF_PARTS = [
  { key: "icp", label: "ICP · who qualifies, and who you disqualify", navLabel: "ICP", Icon: Users, accent: "var(--blue-600)" },
  { key: "pain", label: "Pain & do-nothing cost · what it costs them to stand still", navLabel: "Pain", Icon: AlertTriangle, accent: "var(--red-600, #c0392b)" },
  { key: "worldview", label: "Buyer psychology · what is running in their head before you speak", navLabel: "Buyer psychology", Icon: Brain, accent: "var(--c-700)" },
  { key: "pitch", label: "Positioning · the first thirty seconds and the two minutes after", navLabel: "Positioning", Icon: Megaphone, accent: "var(--green-600)" },
];
// The fifth chip. Paired deliberately with Buyer psychology — one is what is
// happening in their head, this is what has to be true in yours.
const LAWS_PART = { label: "Operator psychology · the ten laws", Icon: Compass, accent: "var(--gold-600, #a06d10)" };

function BriefSection({ brief, band, stageCount }) {
  // Called before ANY early return, and never inside a conditional branch —
  // this hook was originally written inline in the physics chip's JSX, which
  // works only while every band of every playbook has physics content. The
  // first band without it would change the hook count mid-render and crash
  // the whole page. Hoisted here so the hook always runs; whether the chip
  // renders stays a pure display decision below.
  const flashPhysics = useChangeFlash(
    brief ? JSON.stringify((brief.band_physics || {})[band] || null) : "",
    band,
  );
  if (!brief) return null;
  const has = (k) => (brief[`${k}_headline`] || brief[`${k}_body`] || (brief[`${k}_bullets`] || []).length);
  const parts = BRIEF_PARTS.filter((p) => has(p.key));
  const laws = brief.laws || [];
  const hasAnything = parts.length || laws.length
    || (brief.scenario_modes || {}).modes || (brief.competitive || {}).map;
  // Only the selected band's physics. "One person decides" stops being true the
  // moment you leave the smallest band, so showing all four would teach four
  // contradictory things at once.
  const physics = (brief.band_physics || {})[band];
  const bandMeta = BANDS.find((x) => x.key === band);
  const comp = brief.competitive || null;
  const modes = brief.scenario_modes || null;
  // Banao currently has modes and bands but no ICP or stages yet, so the brief
  // must render on the strength of any one of its chips rather than requiring
  // the four written ones.
  if (!hasAnything) return null;

  return (
    <>
      <SectionHeading
        title="Before the first call"
        note={`Everything the ${stageCount} stages already take for granted.`}
      />
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {/* First, deliberately — before who it is for, before what hurts. On a
            product that sells across six different buyer scenarios, the mode is
            the first diagnosis and everything after it depends on which one is
            true. Getting the band right and the mode wrong runs the wrong
            meeting five times out of six. */}
        {modes && (modes.modes || []).length > 0 && (
          <Collapsible
            stateKey={`${brief.slug}:modes`}
            navId={`nav-${brief.slug}-modes`}
            navLabel="Scenario mode"
            eyebrow={modes.heading}
            Icon={Compass}
            accent="var(--blue-600)"
            headline={modes.subhead}
          >
            {modes.modes.map((m, i) => (
              <div key={i} style={{ borderTop: i ? "1px solid var(--c-200)" : "none", paddingTop: i ? 10 : 0, marginTop: i ? 10 : 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <strong style={{ fontSize: 13, color: "var(--c-900)" }}>{m.mode}</strong>
                  {/* A safety rule, not a formatting choice — so it reads as one. */}
                  {m.senior_only && (
                    <span style={{ fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: ".05em", color: "var(--red-600, #c0392b)", border: "1px solid var(--red-500, #c0392b)", borderRadius: 999, padding: "1px 8px" }}>
                      Senior only
                    </span>
                  )}
                </div>
                <p style={{ margin: "4px 0 0", fontSize: 12.5, lineHeight: 1.6, ...muted }}>{m.shape}</p>
                <div style={{ marginTop: 5, fontSize: 12.5, lineHeight: 1.6 }}>
                  <div><strong style={{ color: "var(--c-700)" }}>Posture — </strong><span style={muted}>{m.posture}</span></div>
                  <div><strong style={{ color: "var(--c-700)" }}>Proof — </strong><span style={muted}>{m.bridge_case}</span></div>
                  <div><strong style={{ color: "var(--red-600, #c0392b)" }}>Drift — </strong><span style={muted}>{m.drift}</span></div>
                  <div><strong style={{ color: "var(--c-700)" }}>Who runs it — </strong><span style={muted}>{m.who_runs}</span></div>
                </div>
              </div>
            ))}
            {!!(modes.rules || []).length && (
              <div style={{ marginTop: 12, border: "1px solid var(--red-500, #c0392b)", borderRadius: 10, padding: "11px 14px" }}>
                <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".04em", fontWeight: 700, color: "var(--red-600, #c0392b)", marginBottom: 4 }}>
                  Not negotiable
                </div>
                {modes.rules.map((r, i) => (
                  <p key={i} style={{ margin: i ? "6px 0 0" : 0, fontSize: 12.5, lineHeight: 1.6, color: "var(--c-800)" }}>{r}</p>
                ))}
              </div>
            )}
            {!!(modes.stage_effects || []).length && (
              <div style={{ marginTop: 12 }}>
                <strong style={{ fontSize: 11.5, color: "var(--c-700)" }}>Where the mode changes the stage</strong>
                {modes.stage_effects.map((s, i) => (
                  <div key={i} style={{ fontSize: 12.5, lineHeight: 1.6, marginTop: 4, ...muted }}>
                    <strong style={{ color: "var(--c-800)" }}>{s.stage}</strong> — {s.effect}
                  </div>
                ))}
              </div>
            )}
            {/* Six modes exist is only half the answer. A junior immediately
                needs to know which two they will actually meet — that is what
                turns the senior-only rule from a restriction into a description
                of deals that never reach them. */}
            {modes.matrix && (
              <div style={{ marginTop: 12 }}>
                <strong style={{ fontSize: 11.5, color: "var(--c-700)" }}>{modes.matrix.heading}</strong>
                <div style={{ overflowX: "auto", marginTop: 5 }}>
                  <table style={{ borderCollapse: "collapse", fontSize: 12, minWidth: 420 }}>
                    <thead>
                      <tr>{modes.matrix.columns.map((c, i) => (
                        <th key={i} style={{ textAlign: i ? "center" : "left", padding: "3px 9px", color: "var(--c-600)", fontWeight: 700, borderBottom: "1px solid var(--c-200)" }}>{c}</th>
                      ))}</tr>
                    </thead>
                    <tbody>
                      {modes.matrix.rows.map((r, i) => (
                        <tr key={i}>
                          <td style={{ padding: "3px 9px", ...muted }}>{r.mode}</td>
                          {r.cells.map((c, j) => (
                            <td key={j} style={{ padding: "3px 9px", textAlign: "center", fontWeight: c === "primary" ? 800 : 400, color: c === "primary" ? "var(--blue-600)" : (c === "—" ? "var(--c-400, #aaa)" : "var(--c-600)") }}>{c}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {modes.matrix.note && (
                  <p style={{ margin: "6px 0 0", fontSize: 12.5, lineHeight: 1.6, ...muted }}>{modes.matrix.note}</p>
                )}
              </div>
            )}
            {modes.readiness && (
              <div style={{ marginTop: 12, background: "var(--surface-soft, rgba(120,120,120,.05))", borderRadius: 8, padding: "11px 13px" }}>
                <strong style={{ fontSize: 12.5, color: "var(--c-900)" }}>{modes.readiness.heading}</strong>
                <p style={{ margin: "4px 0 0", fontSize: 12.5, lineHeight: 1.6, ...muted }}>{modes.readiness.body}</p>
                {(modes.readiness.states || []).map((s, i) => (
                  <div key={i} style={{ marginTop: 6, fontSize: 12.5, lineHeight: 1.6 }}>
                    <strong style={{ color: "var(--c-800)" }}>{s.state}</strong>
                    <span style={muted}> — {s.sounds_like} → {s.you_do}</span>
                  </div>
                ))}
                {modes.readiness.guardrail && (
                  <p style={{ margin: "8px 0 0", fontSize: 12.5, lineHeight: 1.6, fontWeight: 600, color: "var(--c-900)" }}>
                    {modes.readiness.guardrail}
                  </p>
                )}
              </div>
            )}
            {/* The smallest band is a training ground where people genuinely
                close. This is what makes it a graduation frame rather than a
                beginner one — readiness is demonstrated, not served. */}
            {modes.graduation && (
              <div style={{ marginTop: 12, border: "1px solid var(--green-500, #2e9e6b)", borderRadius: 10, padding: "11px 14px" }}>
                <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".04em", fontWeight: 700, color: "var(--green-600)", marginBottom: 4 }}>
                  {modes.graduation.heading}
                </div>
                <p style={{ margin: 0, fontSize: 12.5, lineHeight: 1.6, ...muted }}>{modes.graduation.body}</p>
                <Bullets items={modes.graduation.signals} icon={(c) => <Target size={11} color={c} />} color="var(--green-600)" />
                {!!(modes.graduation.not_carried_up || []).length && (
                  <div style={{ marginTop: 8 }}>
                    <strong style={{ fontSize: 11.5, color: "var(--c-700)" }}>What does not travel upward</strong>
                    <Bullets items={modes.graduation.not_carried_up} icon={(c) => <Ban size={11} color={c} />} color="var(--red-600, #c0392b)" />
                  </div>
                )}
              </div>
            )}
          </Collapsible>
        )}

        {parts.map(({ key, label, navLabel, Icon, accent }) => (
          <Collapsible
            key={key}
            stateKey={`${brief.slug}:${key}`}
            navId={`nav-${brief.slug}-${key}`}
            navLabel={navLabel}
            eyebrow={label}
            Icon={Icon}
            accent={accent}
            headline={<Glossed path={`brief.${key}_headline`}>{brief[`${key}_headline`]}</Glossed>}
          >
            {/* The line reached for in the first thirty seconds, set apart so it
                can be found without reading the section around it. */}
            {key === "pitch" && brief.pitch_one_liner && (
              <blockquote style={{ margin: "0 0 10px", padding: "10px 14px", borderLeft: "3px solid var(--green-600)", background: "var(--surface-soft, rgba(120,120,120,.05))", borderRadius: "0 8px 8px 0", fontSize: 13.5, lineHeight: 1.65, color: "var(--c-900)" }}>
                <Glossed path="brief.pitch_one_liner">{brief.pitch_one_liner}</Glossed>
              </blockquote>
            )}
            <Para text={brief[`${key}_body`]} path={`brief.${key}_body`} />
            <Bullets items={brief[`${key}_bullets`]} icon={(c) => <CheckSquare size={11} color={c} />} color={accent} pathPrefix={`brief.${key}_bullets`} />
            {key === "icp" && !!(brief.icp_disqualifiers || []).length && (
              <div style={{ marginTop: 10 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <Ban size={13} color="var(--red-600, #c0392b)" />
                  <strong style={{ fontSize: 11.5, color: "var(--c-700)" }}>Disqualify on sight</strong>
                </div>
                <Bullets items={brief.icp_disqualifiers} icon={(c) => <Ban size={11} color={c} />} color="var(--red-600, #c0392b)" pathPrefix="brief.icp_disqualifiers" />
              </div>
            )}
            {key === "pitch" && brief.pitch_trap && (
              <div style={{ marginTop: 10, border: "1px solid var(--red-500, #c0392b)", borderRadius: 10, padding: "11px 14px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                  <AlertTriangle size={13} color="var(--red-600, #c0392b)" />
                  <strong style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".04em", color: "var(--red-600, #c0392b)" }}>Do not lead with</strong>
                </div>
                <Para text={brief.pitch_trap} size={12.5} path="brief.pitch_trap" />
              </div>
            )}
          </Collapsible>
        ))}

        {/* The compressed truths, as the fifth chip. Everything in the stages
            is downstream of these, so they are stated once rather than
            re-argued inside twelve stages. */}
        {!!laws.length && (
          <Collapsible
            stateKey={`${brief.slug}:laws`}
            navId={`nav-${brief.slug}-laws`}
            navLabel="Operator laws"
            eyebrow={LAWS_PART.label}
            Icon={LAWS_PART.Icon}
            accent={LAWS_PART.accent}
            headline={brief.laws_headline || "The compressed truths. Everything else is downstream of these."}
          >
            <div style={{ display: "flex", flexDirection: "column", gap: 11 }}>
              {laws.map((law, i) => (
                <div key={i} style={{ display: "flex", gap: 11, alignItems: "flex-start" }}>
                  <span style={{ fontSize: 11, fontWeight: 800, color: LAWS_PART.accent, minWidth: 20, marginTop: 2 }}>{i + 1}</span>
                  <p style={{ margin: 0, fontSize: 12.5, lineHeight: 1.65, color: "var(--c-800)" }}>
                    <strong style={{ color: "var(--c-900)" }}>{law.name}. </strong>
                    <Glossed path={`laws.${i}`}>{law.body}</Glossed>
                  </p>
                </div>
              ))}
            </div>
          </Collapsible>
        )}

        {/* Who else is in the room. Product-specific by nature — Universal has
            no product, so it has no competitors to name, and a competitive
            section with no competitors in it is theory. */}
        {comp && (comp.map || []).length > 0 && (
          <Collapsible
            stateKey={`${brief.slug}:competitive`}
            navId={`nav-${brief.slug}-competitive`}
            navLabel="Competitive position"
            eyebrow="Competitive position · you are never the only conversation"
            Icon={Swords}
            accent="var(--red-600, #c0392b)"
            headline={comp.headline}
          >
            <Para text={comp.body} size={12.5} />
            <div style={{ marginTop: 12, overflowX: "auto" }}>
              <table style={{ borderCollapse: "collapse", fontSize: 12, width: "100%", minWidth: 520 }}>
                <tbody>
                  {comp.map.map((g, i) => (
                    <tr key={i} style={{ borderTop: "1px solid var(--c-200)" }}>
                      <td style={{ padding: "8px 10px 8px 0", verticalAlign: "top", minWidth: 130 }}>
                        <div style={{ fontWeight: 700, color: "var(--c-900)" }}>{g.group}</div>
                        <div style={{ fontSize: 11.5, ...muted }}>{g.who}</div>
                      </td>
                      <td style={{ padding: "8px 10px", verticalAlign: "top", lineHeight: 1.6, ...muted }}>
                        {g.position}
                        <div style={{ marginTop: 4, color: "var(--c-800)" }}>{g.line}</div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {comp.not_a_competitor && (
              <div style={{ marginTop: 12, border: "1px solid var(--blue-500, #4a7dff)", borderRadius: 10, padding: "11px 14px" }}>
                <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".04em", fontWeight: 700, color: "var(--blue-600)", marginBottom: 4 }}>
                  Not a competitor — {comp.not_a_competitor.who}
                </div>
                <p style={{ margin: 0, fontSize: 12.5, lineHeight: 1.6, ...muted }}>{comp.not_a_competitor.why}</p>
                <p style={{ margin: "6px 0 0", fontSize: 12.5, lineHeight: 1.6, color: "var(--c-900)" }}>{comp.not_a_competitor.line}</p>
              </div>
            )}
            {!!(comp.wins || []).length && (
              <div style={{ marginTop: 12 }}>
                <strong style={{ fontSize: 11.5, color: "var(--c-700)" }}>Where we genuinely win</strong>
                <Bullets items={comp.wins} icon={(c) => <CheckSquare size={11} color={c} />} color="var(--green-600)" />
              </div>
            )}
            {/* The half that makes the other half credible. A junior who cannot
                name a weakness is not trusted on the strengths. */}
            {!!(comp.limits || []).length && (
              <div style={{ marginTop: 12 }}>
                <strong style={{ fontSize: 11.5, color: "var(--c-700)" }}>Where we genuinely lose</strong>
                {comp.limits.map((l, i) => (
                  <div key={i} style={{ marginTop: 7 }}>
                    <div style={{ fontSize: 12.5, fontWeight: 600, color: "var(--c-900)" }}>{l.limit}</div>
                    <div style={{ fontSize: 12.5, lineHeight: 1.6, ...muted }}>{l.answer}</div>
                  </div>
                ))}
              </div>
            )}
            {!!(comp.drifts || []).length && (
              <div style={{ marginTop: 12 }}>
                <strong style={{ fontSize: 11.5, color: "var(--c-700)" }}>The five moments that turn a position into a comparison</strong>
                {comp.drifts.map((d, i) => (
                  <div key={i} style={{ marginTop: 7 }}>
                    <div style={{ fontSize: 12.5, fontWeight: 600, color: "var(--c-900)" }}>{d.name}</div>
                    <div style={{ fontSize: 12.5, lineHeight: 1.6, ...muted }}>{d.recovery}</div>
                  </div>
                ))}
              </div>
            )}
            {!!(comp.homework || []).length && (
              <div style={{ marginTop: 12 }}>
                <strong style={{ fontSize: 11.5, color: "var(--c-700)" }}>The standing homework</strong>
                <Bullets items={comp.homework} icon={(c) => <Target size={11} color={c} />} color="var(--red-600, #c0392b)" />
              </div>
            )}
            {comp.closing && (
              <p style={{ margin: "12px 0 0", fontSize: 13, lineHeight: 1.6, fontWeight: 600, color: "var(--c-900)" }}>{comp.closing}</p>
            )}
          </Collapsible>
        )}

        {/* The physics of this deal size, before any stage is opened.
            Band-scoped, so it changes with the picker. */}
        {physics && (
          <Collapsible
            stateKey={`${brief.slug}:physics`}
            navId={`nav-${brief.slug}-physics`}
            navLabel="Market physics"
            eyebrow={`${bandMeta?.label} market physics · what changes at this deal size`}
            Icon={Gauge}
            accent="var(--blue-600)"
            headline={physics.headline}
          >
            <Flashable flashKey={flashPhysics}>
              <Bullets items={physics.bullets} icon={(c) => <CheckSquare size={11} color={c} />} color="var(--blue-600)" pathPrefix="physics" />
            </Flashable>
          </Collapsible>
        )}
      </div>
    </>
  );
}

// Flags a value as "just changed" for a few seconds right after the market
// band changes, so a reader flipping Small -> Mid -> Large can see at a
// glance which specific lines actually moved instead of re-reading the whole
// card to spot the difference themselves. Only fires on a genuine change of
// THIS value between the old band and the new one — switching to a band
// whose content happens to read identically produces no flash, which is
// itself the correct signal ("nothing here changed at this size").
//
// The band-changed check runs directly in render, comparing against a ref of
// the previous band and the previous value, and calls setState from inside
// that check — the documented React pattern for deriving state from a prop
// change without an extra render's delay (see "adjusting state when a prop
// changes" in the React docs). An effect-based version would let one
// unhighlighted frame paint before the flash turned on.
function useChangeFlash(value, band) {
  const prevBandRef = useRef(band);
  const prevValueRef = useRef(value);
  const [flashKey, setFlashKey] = useState(0);

  if (prevBandRef.current !== band) {
    const changed = prevValueRef.current !== value;
    prevBandRef.current = band;
    if (changed) setFlashKey((k) => k + 1);
  }
  prevValueRef.current = value;

  return flashKey;
}

// Wraps content that should flash pastel yellow, fading in then out over
// about five seconds, whenever `flashKey` advances. Retriggering is done by
// changing the wrapper's `key` rather than toggling a class — React remounts
// the node, which restarts the CSS animation from its first frame the same
// way reloading a GIF does, so no JS timer is needed to turn the highlight
// back off; the animation's own last keyframe (transparent) is where it ends.
function Flashable({ flashKey, inline, children }) {
  if (!flashKey) return children;
  const Tag = inline ? "span" : "div";
  const style = inline
    ? { animation: "playbook-flash 5s ease-out", borderRadius: 999, display: "inline-block" }
    : { animation: "playbook-flash 5s ease-out", borderRadius: 8, margin: "-4px -6px", padding: "4px 6px" };
  return (
    <Tag key={flashKey} style={style}>
      {children}
    </Tag>
  );
}

// Injected once per page (see PlaybookScreen's return). Kept as its own tiny
// component rather than a global stylesheet edit, since this page is the only
// place on the site that uses it.
function FlashKeyframes() {
  // Peak intensity is a theme decision, not one number. On the light theme
  // the text under the wash is near-black, so a real highlighter yellow
  // reads fine. On dark the text is light grey — the same wash turned it
  // muddy-on-mustard and genuinely hard to read (seen in the screenshot
  // pass, not guessed) — so dark gets a subtle warm glow instead.
  // `index.html` guarantees data-theme is set on <html> before first paint,
  // so the selector is reliable from the very first frame.
  return (
    <style>{`
      :root { --playbook-flash-peak: rgba(255, 213, 79, .45); }
      :root[data-theme="dark"] { --playbook-flash-peak: rgba(255, 213, 79, .17); }
      @keyframes playbook-flash {
        0%   { background-color: transparent; }
        12%  { background-color: var(--playbook-flash-peak); }
        70%  { background-color: var(--playbook-flash-peak); }
        100% { background-color: transparent; }
      }
    `}</style>
  );
}

function StageCard({ stage, band, isLast }) {
  const [open, setOpen] = useOpenState(`stage:${stage.id}`);
  const meta = BANDS.find((x) => x.key === band);
  // Band content is resolved server-side, so this never implements the fallback
  // rules. Falling back to the stage-level columns keeps playbooks that have no
  // band content yet — InterviewGod — rendering exactly as before.
  const bc = (stage.band_content || {})[band] || {};
  const bsrc = (stage.band_sources || {})[band] || {};
  const pick = (key) => (bc[key] !== undefined ? bc[key] : stage[key]);
  const applies = bc.applies || "full";
  // "Merged" is not "off". The stage happens — it just has no separate moment,
  // because first touch and earning the conversation are one message thread at
  // the smallest band, and diagnosis and validation are one sitting. Rendering
  // it as off would teach a shorter ladder than the one that exists.
  const isMerged = applies === "merged";
  const isOff = applies === "off" || isMerged;
  const dropOff = bc.drop_off_rate !== undefined ? bc.drop_off_rate : stage.drop_off_rate;
  const labelOf = (key) => BANDS.find((x) => x.key === bsrc[key])?.label;

  // Whether this band wrote anything of its own for this stage. A stage is
  // either the band's or inherited — never half of each. Mixing them printed
  // "your champion needs a deck" eleven lines above "do not build a deck",
  // because the buyer-reality column and the band content disagreed and both
  // rendered at full strength.
  const ownsBand = !!(stage.bands || {})[band];
  const writtenFor = bsrc.posture || bsrc.guardrails;
  const inherited = band !== "small" && !ownsBand && writtenFor && writtenFor !== band;
  // Suppressed until this band has moves to go with its buyer paragraph. It
  // comes back on its own the moment the band gets content.
  const brBand = ownsBand ? band : "small";

  // A stage that does not run here stays in the list. Removing it would teach a
  // shorter ladder than the one that exists.
  const navId = `nav-stage-${stage.id}`;

  // brBand, not band: while a stage has no content of its own here, the buyer
  // paragraph is held back with everything else so the card speaks in exactly
  // one voice. Computed once so both the flash comparison below and the
  // render further down use the same value.
  const br = bandText(stage, brBand);

  // Every field that actually varies by band, flagged so its block can flash
  // when the reader switches bands and this particular line moved. Called
  // unconditionally, before the isOff early return, because hooks cannot be
  // conditional — an off stage simply never uses the flash keys it computes.
  const flashPosture = useChangeFlash(bc.posture || "", band);
  const flashCycle = useChangeFlash(bc.cycle || "", band);
  const flashStageNote = useChangeFlash(bc.stage_note || "", band);
  const flashBuyerReality = useChangeFlash(br?.text || "", band);
  const flashGuardrails = useChangeFlash(JSON.stringify(pick("guardrails") || []), band);
  const flashBestPractices = useChangeFlash(JSON.stringify(pick("best_practices") || []), band);
  const flashChecklist = useChangeFlash(JSON.stringify(pick("checklist_items") || []), band);
  const flashWorked = useChangeFlash(JSON.stringify(bc.worked_example || null), band);

  if (isOff) {
    return (
      <div id={navId} data-nav-label={stage.name} data-nav-number={stage.stage_number} style={{ ...card, opacity: 0.72, scrollMarginTop: 74 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: "var(--c-500)", minWidth: 22 }}>{stage.stage_number}</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: "var(--c-600)" }}>{stage.name}</div>
            <div style={{ fontSize: 12, ...muted }}>{stage.subtitle}</div>
          </div>
          <span style={{ fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".05em", color: isMerged ? "var(--blue-600)" : "var(--c-500)", border: `1px solid ${isMerged ? "var(--blue-500, #4a7dff)" : "var(--c-200)"}`, borderRadius: 999, padding: "2px 9px", whiteSpace: "nowrap" }}>
            {isMerged ? `Runs inside ${bc.merged_into}` : `Not at ${meta?.label}`}
          </span>
        </div>
        {bc.stage_note && (
          <Flashable flashKey={flashStageNote}>
            <p style={{ margin: "9px 0 0 32px", fontSize: 12.5, lineHeight: 1.65, ...muted }}>
              <Glossed path={`stage.${stage.id}.band.stage_note`}>{bc.stage_note}</Glossed>
              {/* The band name is filled in here rather than written into the
                  content, so the pointer stays true at every band. */}
              {bc.next_band_hint && nextBandLabel(band) && (
                <> {bc.next_band_hint.replace("{next_band}", nextBandLabel(band))}</>
              )}
            </p>
          </Flashable>
        )}
      </div>
    );
  }

  return (
    <div id={navId} data-nav-label={stage.name} data-nav-number={stage.stage_number} style={{ ...card, scrollMarginTop: 74 }}>
      <button
        onClick={() => setOpen((v) => !v)}
        style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", background: "transparent", border: "none", cursor: "pointer", textAlign: "left", padding: 0 }}
      >
        <span style={{ fontSize: 11, fontWeight: 700, color: "var(--blue-600)", minWidth: 22 }}>{stage.stage_number}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: "var(--c-900)" }}>
            {stage.name}
            {stage.public_ref ? (
              <sup title={stage.public_ref} style={{ marginLeft: 6, fontSize: 10, fontWeight: 650, color: "var(--c-500)", cursor: "help" }}>ref</sup>
            ) : null}
          </div>
          {stage.subtitle && <div style={{ fontSize: 12, ...muted }}>{stage.subtitle}</div>}
          {stage.public_ref ? <div style={{ fontSize: 11.5, marginTop: 2, ...muted }}>{stage.public_ref}</div> : null}
        </div>
        {/* What "normal" looks like at this size — how long, how many touches,
            which channel. Quiet, next to the drop-off figure. */}
        {bc.cycle && (
          <Flashable flashKey={flashCycle} inline>
            <span style={{ fontSize: 10.5, fontWeight: 600, color: "var(--c-600)", background: "var(--surface-soft, rgba(120,120,120,.07))", borderRadius: 999, padding: "3px 10px", whiteSpace: "nowrap" }}>
              {bc.cycle}
            </span>
          </Flashable>
        )}
        {typeof dropOff === "number" && (
          <span style={{ fontSize: 11, fontWeight: 700, color: "var(--c-500)" }}>{dropOff}% drop-off</span>
        )}
        {open ? <ChevronDown size={16} color="var(--c-500)" /> : <ChevronRight size={16} color="var(--c-500)" />}
      </button>

      {open && (
        <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 10 }}>
          {inherited && (
            <div style={{ fontSize: 11.5, ...muted, fontStyle: "italic" }}>
              Not written for {meta?.label} yet — showing {BANDS.find((x) => x.key === writtenFor)?.label}.
            </div>
          )}
          {bc.stage_note && (
            <Flashable flashKey={flashStageNote}>
              <p style={{ margin: 0, fontSize: 12.5, lineHeight: 1.65, ...muted }}>
                <Glossed path={`stage.${stage.id}.band.stage_note`}>{bc.stage_note}</Glossed>
              </p>
            </Flashable>
          )}
          {/* Labelled with the band the words came FROM, never the band being
              viewed. "At Mid" over Small's text is a more specific claim than
              the banner above it, and it is the wrong one. */}
          {bc.posture && (
            <Flashable flashKey={flashPosture}>
              <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
                <Compass size={14} color="var(--gold-600, #a06d10)" style={{ marginTop: 2, flexShrink: 0 }} />
                <p style={{ margin: 0, fontSize: 12.5, lineHeight: 1.6, ...muted }}>
                  <strong style={{ color: "var(--c-700)" }}>At {labelOf("posture") || meta?.label} — </strong>
                  <Glossed path={`stage.${stage.id}.band.posture`}>{bc.posture}</Glossed>
                </p>
              </div>
            </Flashable>
          )}
          {stage.definition && (
            <p style={{ margin: 0, fontSize: 13, lineHeight: 1.6, color: "var(--c-800)" }}>
              <Glossed path={`stage.${stage.id}.definition`}>{stage.definition}</Glossed>
            </p>
          )}
          {stage.must_win_mindset && (
            <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
              <Brain size={14} color="var(--blue-600)" style={{ marginTop: 2, flexShrink: 0 }} />
              <p style={{ margin: 0, fontSize: 12.5, lineHeight: 1.6, ...muted }}>
                <strong style={{ color: "var(--c-700)" }}>Must-win mindset — </strong>
                <Glossed path={`stage.${stage.id}.must_win_mindset`}>{stage.must_win_mindset}</Glossed>
              </p>
            </div>
          )}
          {/* Deals rarely die where they appear to die — they die at an earlier
              gate that was never really cleared. This is the most load-bearing
              thing on the stage, so it gets its own bordered block rather than
              sitting in a bullet list with everything else. */}
          {/* The title names the failure — "Hidden failure — Wrong person" — so
              the reader gets the point from the heading instead of a line of
              framing repeated identically on all twelve stages. Where it
              surfaces is a named stage, never a number. */}
          {stage.hidden_failure && (
            <div style={{ border: "1px solid var(--red-500, #c0392b)", borderRadius: 10, padding: "11px 14px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                <AlertTriangle size={13} color="var(--red-600, #c0392b)" />
                <strong style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".04em", color: "var(--red-600, #c0392b)" }}>
                  Hidden failure{stage.hidden_failure_name ? ` — ${stage.hidden_failure_name}` : ""}
                  {stage.hidden_failure_shows_up && (
                    <span style={{ fontWeight: 500, textTransform: "none", letterSpacing: 0, color: "var(--c-600)" }}>
                      {" "}(shows up in {stage.hidden_failure_shows_up})
                    </span>
                  )}
                </strong>
              </div>
              <p style={{ margin: 0, fontSize: 12.5, lineHeight: 1.6, color: "var(--c-800)" }}>
                <Glossed path={`stage.${stage.id}.hidden_failure`}>{stage.hidden_failure}</Glossed>
              </p>
            </div>
          )}
          {/* An open item, shown deliberately. A stage reasoned from the
              universal spine rather than a real recorded session says so, so
              nobody mistakes a sensible guess for something that's been ruled on. */}
          {stage.todo && (
            <div style={{ border: "1px dashed var(--gold-500, #d9a441)", borderRadius: 10, padding: "11px 14px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                <ClipboardList size={13} color="var(--gold-600, #a06d10)" />
                <strong style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".04em", color: "var(--gold-600, #a06d10)" }}>Open</strong>
              </div>
              <p style={{ margin: 0, fontSize: 12.5, lineHeight: 1.6, color: "var(--c-800)" }}>{stage.todo}</p>
            </div>
          )}
          {stage.icp_context && (
            <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
              <Users size={14} color="var(--c-500)" style={{ marginTop: 2, flexShrink: 0 }} />
              <p style={{ margin: 0, fontSize: 12.5, lineHeight: 1.6, ...muted }}><strong style={{ color: "var(--c-700)" }}>ICP context — </strong>{stage.icp_context}</p>
            </div>
          )}
          {br && (
            <Flashable flashKey={flashBuyerReality}>
              <div style={{ background: "var(--surface-soft, rgba(120,120,120,.05))", borderRadius: 8, padding: "11px 13px" }}>
                <div style={{ fontSize: 10.5, textTransform: "uppercase", letterSpacing: ".04em", color: "var(--blue-600)", fontWeight: 700, marginBottom: 4 }}>
                  {BANDS.find((b) => b.key === br.from)?.label} buyer
                </div>
                <div style={{ fontSize: 12.5, lineHeight: 1.6, ...muted }}>{br.text}</div>
              </div>
            </Flashable>
          )}
          {!!(pick("guardrails") || []).length && (
            <Flashable flashKey={flashGuardrails}>
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <ShieldAlert size={13} color="var(--red-600, #c0392b)" />
                  <strong style={{ fontSize: 11.5, color: "var(--c-700)" }}>Guardrails</strong>
                </div>
                <Bullets items={pick("guardrails")} icon={(c) => <ShieldAlert size={11} color={c} />} color="var(--red-600, #c0392b)" pathPrefix={`stage.${stage.id}.guardrails`} />
              </div>
            </Flashable>
          )}
          {!!(pick("best_practices") || []).length && (
            <Flashable flashKey={flashBestPractices}>
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <CheckSquare size={13} color="var(--green-600)" />
                  <strong style={{ fontSize: 11.5, color: "var(--c-700)" }}>Best practices</strong>
                </div>
                <Bullets items={pick("best_practices")} icon={(c) => <CheckSquare size={11} color={c} />} color="var(--green-600)" pathPrefix={`stage.${stage.id}.best_practices`} />
              </div>
            </Flashable>
          )}
          {!!(pick("checklist_items") || []).length && (
            <Flashable flashKey={flashChecklist}>
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <Target size={13} color="var(--blue-600)" />
                  {/* Nothing to advance to on the last stage. */}
                  <strong style={{ fontSize: 11.5, color: "var(--c-700)" }}>{isLast ? "Signs it worked" : "Before advancing"}</strong>
                </div>
                <Bullets items={pick("checklist_items")} icon={(c) => <Target size={11} color={c} />} color="var(--blue-600)" pathPrefix={`stage.${stage.id}.checklist_items`} />
              </div>
            </Flashable>
          )}
          {/* One worked example per stage, where the failure is specific enough
              that naming the move is not enough on its own. */}
          {bc.worked_example && (
            <Flashable flashKey={flashWorked}>
              <div style={{ border: "1px solid var(--blue-500, #4a7dff)", borderRadius: 10, padding: "11px 14px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 5 }}>
                  <Lightbulb size={13} color="var(--blue-600)" />
                  <strong style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".04em", color: "var(--blue-600)" }}>
                    {bc.worked_example.title}
                  </strong>
                </div>
                <Para text={bc.worked_example.body} size={12.5} path={`stage.${stage.id}.band.worked_example`} />
              </div>
            </Flashable>
          )}
          {/* What this stage teaches at this band that carries upward. Only the
              training-ground band writes it, and it is the difference between
              "the easy version" and "the band where you learn the thing". */}
          {bc.carries_upward && (
            <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
              <Target size={14} color="var(--green-600)" style={{ marginTop: 2, flexShrink: 0 }} />
              <p style={{ margin: 0, fontSize: 12.5, lineHeight: 1.6, ...muted }}>
                <strong style={{ color: "var(--green-600)" }}>Carries upward — </strong>{bc.carries_upward}
              </p>
            </div>
          )}
          <SubstepList stage={stage} band={band} only={bc.substeps} />
        </div>
      )}
    </div>
  );
}

// Substeps that fire at this band. Filtered numbers are NOT renumbered — 0.2 and
// 0.3 running with 0.1 visibly absent is the teaching moment, and the line below
// names what is missing so its absence reads as a decision.
function SubstepList({ stage, band, only }) {
  const all = stage.substeps || [];
  if (!all.length) return null;
  const keep = Array.isArray(only) && only.length ? only : null;
  const shown = keep ? all.filter((s) => keep.includes(s.number)) : all;
  const hidden = keep ? all.filter((s) => !keep.includes(s.number)) : [];
  const meta = BANDS.find((x) => x.key === band);
  return (
    <div style={{ marginTop: 4 }}>
      {shown.map((s) => <Substep key={s.id} step={s} band={band} />)}
      {hidden.map((s) => (
        <div key={s.id} style={{ borderTop: "1px solid var(--c-200)", padding: "8px 0 8px 22px", fontSize: 12, ...muted }}>
          <span style={{ fontWeight: 600 }}>{s.number} {s.title}</span> does not run at {meta?.label}.
        </div>
      ))}
    </div>
  );
}

// "On this page" — a plain, always-visible list of section names in the right
// margin, the current one picked out with a bar and darker text. No hover
// mechanic at all: the second attempt at this (a compact tick rail with a
// name that appeared only on hover) was rejected twice, and the actual
// reference asked for — a real sidebar, not a floating overlay — never
// needed hovering in the first place, because the labels are just always
// there. Removing the hover removes the whole class of "which tick does this
// label belong to" problem rather than trying to fix it a third time.
//
// A real second column in the page's own layout (see the flex row around
// `contentRef` below), not a fixed overlay — that is what lets this use
// `position: sticky` the same way every other sidebar in this app already
// does, rather than a floating element with its own z-index to manage.
//
// Self-discovering rather than hand-listed: it reads whatever carries a
// `data-nav-label` inside the given container, in DOM order, and re-scans
// whenever `watch` changes. A hand-maintained list of sections would drift the
// moment a stage goes off at one band and not another — which happens on
// every band change on this exact page.
function SectionNav({ containerRef, watch, band, bizKey, restoreId, onActiveChange }) {
  const [sections, setSections] = useState([]);
  const [activeId, setActiveId] = useState(null);
  // The line below the TOP OF THE SCROLL PANEL a section's heading has to
  // cross to count as "current" — kept as one constant because it has to
  // agree with `scrollMarginTop` below and with `jump()`'s own landing spot,
  // or a click and the highlight it produces disagree with each other.
  const THRESHOLD = 74;

  const scrollerOf = () => {
    const el = nearestScroller(containerRef.current);
    if (el) return el;
  };

  useEffect(() => {
    const root = containerRef.current;
    if (!root) { setSections([]); return; }
    const nodes = Array.from(root.querySelectorAll("[data-nav-label]"));
    setSections(nodes.map((el) => ({
      id: el.id,
      label: el.dataset.navLabel,
      number: el.dataset.navNumber,
    })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watch]);

  // Which section is "current" is decided by walking every section top to
  // bottom and keeping whichever one's heading most recently scrolled above
  // the threshold line — not by which sections merely overlap some detection
  // band. A stage card here can run several screens tall, so once a reader is
  // deep inside one, its own bottom half can still be poking through a top-of-
  // viewport band while the NEXT section has also started — both then read as
  // "intersecting", and picking between them by raw top position (the
  // previous IntersectionObserver approach) picked whichever one the reader
  // had scrolled furthest INTO, which is backwards. That is what put "Pilot"
  // in the rail while "Budget Discussion" filled the screen. Walking in
  // document order and taking the last one that has crossed the line has no
  // such ambiguity, regardless of how tall any section is.
  // After a rail click, the clicked section IS the answer until the smooth
  // scroll delivers it — recomputing from positions mid-flight would sweep
  // the highlight through every section in between, and for a section near
  // the bottom of the page (which can never physically reach the top line)
  // it would land on the wrong entry forever. The lock clears when the
  // target arrives, or the moment the reader scrolls by hand.
  const clickLockRef = useRef(null);

  useEffect(() => {
    if (!sections.length) { setActiveId(null); return undefined; }
    const recompute = () => {
      // Positions are measured from the scroll panel's own top edge, NOT the
      // viewport's. The panel starts below the app's top bar, so a section
      // scrolled flush to the top of the panel still has a viewport-top of
      // panelTop + margin — measuring against the viewport read every such
      // section as "not there yet" and marked the one BEFORE it, which is
      // exactly the off-by-one the reader saw everywhere: highlight one
      // behind on scroll and on click, and each band switch "restoring" the
      // one-behind value, stepping the page back a section per switch.
      const base = (scrollerOf()?.getBoundingClientRect().top ?? 0) + THRESHOLD + 6;
      const lock = clickLockRef.current;
      if (lock) {
        const el = document.getElementById(lock);
        if (el && el.getBoundingClientRect().top > base) {
          setActiveId(lock); // still travelling — hold the clicked answer
          return;
        }
        clickLockRef.current = null; // arrived (or gone) — resume tracking
      }
      let current = sections[0].id;
      for (const s of sections) {
        const el = document.getElementById(s.id);
        if (!el) continue;
        if (el.getBoundingClientRect().top <= base) current = s.id;
        else break; // sections are in document order; none after this one qualify either
      }
      setActiveId(current);
    };
    let raf = null;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => { raf = null; recompute(); });
    };
    // The reader taking over scrolling cancels a click lock: wheel and touch
    // are their scrolling, not the browser finishing our smooth scroll.
    const onUserScroll = () => { clickLockRef.current = null; };
    recompute();
    // Capture phase, not bubble: the panel's scroll events never reach
    // `window` by bubbling (scroll does not bubble), but a capturing listener
    // sees them on the way down regardless of which element owns the
    // scrollbar. Without this the listener fired exactly once, at mount —
    // the frozen-highlight bug.
    window.addEventListener("scroll", onScroll, { passive: true, capture: true });
    window.addEventListener("resize", onScroll);
    window.addEventListener("wheel", onUserScroll, { passive: true, capture: true });
    window.addEventListener("touchmove", onUserScroll, { passive: true, capture: true });
    return () => {
      window.removeEventListener("scroll", onScroll, { capture: true });
      window.removeEventListener("resize", onScroll);
      window.removeEventListener("wheel", onUserScroll, { capture: true });
      window.removeEventListener("touchmove", onUserScroll, { capture: true });
      if (raf) cancelAnimationFrame(raf);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sections]);

  // Report the active section upward so it can be remembered across a
  // reload/re-login, not just across a live band switch. Fires on every
  // change, cheap: the parent only writes it into localStorage on a debounce.
  useEffect(() => {
    onActiveChange?.(activeId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId]);

  // Keep the reader on the same section across a band switch — AND, seeded
  // from `restoreId` below, land on the remembered section the very first
  // time this tab's content appears (fresh page load, or switching back to
  // this tab after a reload). Detected here in render, not in an effect
  // keyed on `band` — this line runs on the very render `band` changes in,
  // while `activeId` still holds whatever it was tracking under the band the
  // reader is LEAVING, before anything has reacted to the new one. Section
  // ids are band-independent (a stage's id is its stage id, not its band),
  // so the same id found in the freshly-rescanned `sections` for the new
  // band — or in a fresh mount's first scan — is genuinely the same stage.
  const prevBandRef = useRef(band);
  const prevBizRef = useRef(bizKey);
  const pendingRestoreId = useRef(restoreId || null);
  if (prevBizRef.current !== bizKey) {
    // A tab switch: land on THAT tab's own remembered section, freshly
    // supplied via `restoreId` — never the outgoing tab's `activeId`, which
    // means nothing on a different tab's content.
    pendingRestoreId.current = restoreId || null;
    prevBizRef.current = bizKey;
    prevBandRef.current = band; // a band difference riding along with the tab switch is not a separate event
  } else if (prevBandRef.current !== band) {
    pendingRestoreId.current = activeId;
    prevBandRef.current = band;
  }

  useLayoutEffect(() => {
    if (!pendingRestoreId.current) return undefined;
    if (!sections.some((s) => s.id === pendingRestoreId.current)) return undefined;
    const id = pendingRestoreId.current;
    pendingRestoreId.current = null;
    // A band switch routinely changes how tall the stages ABOVE the reader
    // are — a stage that is off at Small and full at Large, for instance — so
    // the raw scroll offset that used to land on "Pilot" can now point at
    // something else entirely once the new band's content has laid out. One
    // frame lets that reflow finish before landing back on the same section,
    // and no smooth animation, so it reads as "the page never moved" rather
    // than a visible correction after the fact.
    const raf = requestAnimationFrame(() => {
      document.getElementById(id)?.scrollIntoView({ block: "start" });
    });
    return () => cancelAnimationFrame(raf);
  }, [sections]);

  // Not worth a rail for a page with only one or two things on it.
  if (sections.length < 3) return null;

  const jump = (id) => {
    // The clicked entry highlights NOW, not when the smooth scroll happens to
    // report positions that agree — see clickLockRef above.
    clickLockRef.current = id;
    setActiveId(id);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div style={{ width: 208, flexShrink: 0, position: "sticky", top: 74 }}>
      <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".05em", color: "var(--c-500)", marginBottom: 8, paddingLeft: 13 }}>
        On this page
      </div>
      <div style={{ display: "flex", flexDirection: "column", maxHeight: "72vh", overflowY: "auto" }}>
        {sections.map((s) => {
          const on = s.id === activeId;
          return (
            <button
              key={s.id}
              onClick={() => jump(s.id)}
              style={{
                display: "block", textAlign: "left", border: "none", background: "none", cursor: "pointer",
                padding: "5px 10px 5px 11px", fontSize: 13, lineHeight: 1.4,
                borderLeft: `2px solid ${on ? "var(--blue-600)" : "transparent"}`,
                fontWeight: on ? 700 : 500,
                color: on ? "var(--c-900)" : "var(--c-500)",
              }}
            >
              {s.number != null && s.number !== "" ? `${s.number} ` : ""}{s.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// Universal is the spine and is read first; InterviewGod is the playbook people
// are actually selling from, so it sits second rather than wherever the API
// happened to return it.
const TAB_ORDER = ["universal", "interviewgod"];
function orderTabs(list) {
  return list.slice().sort((a, b) => {
    const ai = TAB_ORDER.indexOf(a.slug), bi = TAB_ORDER.indexOf(b.slug);
    if (ai !== -1 || bi !== -1) return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
    return (a.name || "").localeCompare(b.name || "");
  });
}

export default function PlaybookScreen({ data }) {
  // Superusers are exempt from the copy protections below — they need to be
  // able to right-click, select, print and save the page. Everyone else keeps
  // them, at every employment type: the restriction is about who can take the
  // playbook out of the building, not about seniority or contract.
  //
  // Read from the live session, so while a superuser is viewing the app AS
  // somebody else the protections correctly apply — they are seeing what that
  // person sees.
  const isSuper = !!data?.me?.user?.isSuperuser;

  // The session (and with it, who to key stored state under) can still be
  // resolving on the very first paint — a ref rather than state, so reading it
  // later (inside an async fetch callback, or a scroll handler) always sees
  // whoever is actually logged in by that point, without forcing a re-render
  // every time the session settles.
  const userKeyRef = useRef(computeUserKey(data));
  userKeyRef.current = computeUserKey(data);

  // Read once, synchronously, before the first paint — so the very first render
  // already shows the remembered band and open sections instead of flashing the
  // defaults and then jumping. If the session has not resolved yet at this
  // exact tick, this reads the shared/anonymous bucket instead of the personal
  // one; the very next write goes to the correct bucket once userKeyRef catches
  // up, so this self-corrects rather than needing a reconciliation pass.
  const initialUi = useRef(null);
  if (initialUi.current === null) initialUi.current = loadUiState(userKeyRef.current);

  const [businesses, setBusinesses] = useState(null);
  const [error, setError] = useState("");
  const [loadTry, setLoadTry] = useState(0);
  const [activeBiz, setActiveBizState] = useState(null);
  const [band, setBandState] = useState(initialUi.current.band || "small");
  const [openMap, setOpenMap] = useState(initialUi.current.open || {});
  // Where the reader was, per tab — the three axes together are what "same
  // view as last time" means: which product (activeBiz, above), which market
  // size (band, above), and which position in the table of contents. `sectionId`
  // is the primary answer (survives content changing length between visits —
  // exactly what raw pixels cannot survive, which is the whole lesson of the
  // rail bugs above); `y` is a pixel fallback for a tab with no rail (fewer
  // than three sections — Banao and Vikaas today). Not React state — nothing
  // on screen renders differently while scrolling.
  const sectionByBizRef = useRef(initialUi.current.sectionByBiz || {});
  const scrollByBizRef = useRef(initialUi.current.scrollByBiz || {});
  const scrollSaveTimer = useRef(null);
  // What SectionNav scans for its outline. Scoped to the current business's
  // content only, so switching tabs cannot pick up a stale section from the
  // previous one before the DOM has caught up.
  const contentRef = useRef(null);

  const setOpenFor = useCallback((key, value) => {
    setOpenMap((prev) => {
      if (prev[key] === value) return prev;
      const next = { ...prev, [key]: value };
      patchUiState(userKeyRef.current, { open: next });
      return next;
    });
  }, []);
  const uiContextValue = useMemo(() => ({ openMap, setOpenFor }), [openMap, setOpenFor]);

  const setBand = useCallback((next) => {
    setBandState(next);
    patchUiState(userKeyRef.current, { band: next });
  }, []);

  const flushScrollSave = useCallback(() => {
    patchUiState(userKeyRef.current, {
      sectionByBiz: sectionByBizRef.current,
      scrollByBiz: scrollByBizRef.current,
    });
  }, []);

  // The section rail reports its own active id here directly — see the
  // SectionNav call below — since it already does the (previously bug-prone)
  // work of deciding which section that is. Debounced the same way scrolling
  // is, and flushed on the same triggers.
  const onRailActiveChange = useCallback((id) => {
    if (!activeBiz || !id) return;
    sectionByBizRef.current = { ...sectionByBizRef.current, [activeBiz]: id };
    if (scrollSaveTimer.current) clearTimeout(scrollSaveTimer.current);
    scrollSaveTimer.current = setTimeout(flushScrollSave, 400);
  }, [activeBiz, flushScrollSave]);

  const chooseBiz = useCallback((slug) => {
    // Capture the outgoing tab's position before leaving it — the scroll
    // listener below also saves on a debounce, but the last few pixels before
    // a click might not have hit that debounce yet.
    flushScrollSave();
    setActiveBizState(slug);
    patchUiState(userKeyRef.current, { activeBiz: slug });
  }, [flushScrollSave]);

  // Pixel fallback, for a tab with no outline rail to report a section id
  // (fewer than three sections — SectionNav declines to render at all, see
  // its own early return). Reads and writes the SAME element that actually
  // scrolls this page — the content panel, not `window` — which two earlier
  // bugs on this exact page got wrong; both are named in `nearestScroller`'s
  // own comment so this cannot quietly happen a third time. Capture phase for
  // the same reason as the rail: `scroll` does not bubble, so a listener on
  // `window` only sees it if attached to intercept on the way down.
  useEffect(() => {
    const onScroll = () => {
      if (!activeBiz) return;
      const el = nearestScroller(contentRef.current);
      if (!el) return;
      scrollByBizRef.current = { ...scrollByBizRef.current, [activeBiz]: el.scrollTop };
      if (scrollSaveTimer.current) clearTimeout(scrollSaveTimer.current);
      scrollSaveTimer.current = setTimeout(flushScrollSave, 400);
    };
    window.addEventListener("scroll", onScroll, { passive: true, capture: true });
    window.addEventListener("beforeunload", flushScrollSave);
    return () => {
      window.removeEventListener("scroll", onScroll, { capture: true });
      window.removeEventListener("beforeunload", flushScrollSave);
      if (scrollSaveTimer.current) clearTimeout(scrollSaveTimer.current);
      flushScrollSave();
    };
  }, [activeBiz, flushScrollSave]);

  // Restore the remembered PIXEL fallback whenever the active tab settles —
  // on the very first load, and on every tab switch after. Only runs when
  // this tab has no remembered section id (a rail-less tab, or one visited
  // before this feature existed) — the section-based restore below is
  // strictly more accurate wherever it is available and would otherwise fight
  // this for the same scroll position. One frame of delay (rather than doing
  // this inside the effect body directly) gives layout a chance to finish
  // settling to its real height first, so a long remembered scroll position
  // does not get silently clamped short.
  useLayoutEffect(() => {
    if (!activeBiz) return undefined;
    if (sectionByBizRef.current[activeBiz]) return undefined;
    const y = scrollByBizRef.current[activeBiz];
    if (typeof y === "number") {
      const raf = requestAnimationFrame(() => {
        const el = nearestScroller(contentRef.current);
        if (el) el.scrollTop = y;
      });
      return () => cancelAnimationFrame(raf);
    }
    return undefined;
  }, [activeBiz]);

  // Friction against casual copying of the playbook while this page is open.
  // Shared with the Growth Roadmap — see useCopyProtection for what it
  // actually does and why.
  useCopyProtection(isSuper);

  // Universal stages carry business=None, so they're never nested inside a
  // Business row and can't come back from /Businesses/. Fetched separately and
  // put first — it's the spine every business-specific playbook overlays.
  useEffect(() => {
    let cancelled = false;
    setError("");
    // Neither call swallows its error: a failed load is reported with its real cause, never shown
    // as a missing tab or as an empty playbook.
    Promise.all([
      apiGet("/Playbook/Universal/"),
      apiGet("/Playbook/Businesses/"),
    ])
      .then(([universal, payload]) => {
        if (cancelled) return;
        const list = [];
        if (universal && (universal.stages || []).length) list.push(universal);
        list.push(...unpackList(payload));
        const ordered = orderTabs(list);
        setBusinesses(ordered);
        // Re-read storage fresh here rather than trusting the synchronous read
        // at mount: this fetch is async, and by the time it resolves the
        // session (and the real per-user storage bucket) has almost always
        // caught up, even on a hard page reload.
        const storedBiz = loadUiState(userKeyRef.current).activeBiz;
        const initial = ordered.find((b) => b.slug === storedBiz)?.slug || ordered[0]?.slug || null;
        if (initial) {
          setActiveBizState(initial);
          patchUiState(userKeyRef.current, { activeBiz: initial });
        }
      })
      .catch((err) => !cancelled && setError(errorText(err, "The playbook could not be loaded.")));
    return () => { cancelled = true; };
  }, [loadTry]);

  const current = (businesses || []).find((b) => b.slug === activeBiz);

  // A lead page links here with ?stage=<slug> so someone who was not on the
  // call lands on the playbook move that reading points at.
  useEffect(() => {
    if (!current) return undefined;
    const slug = new URLSearchParams(window.location.search).get("stage");
    if (!slug) return undefined;
    const stage = (current.stages || []).find((item) => item.slug === slug);
    if (!stage) return undefined;
    const timer = window.setTimeout(() => {
      document.getElementById(`nav-stage-${stage.id}`)?.scrollIntoView({ block: "start" });
    }, 60);
    return () => window.clearTimeout(timer);
  }, [current]);
  // Glossary marking is decided once per document rather than tracked while
  // rendering, so which node owns a term never depends on render order.
  const glossary = useMemo(() => buildGlossaryPlan(current, band), [current, band]);

  return (
    <section className="Screen-Stack" style={{ padding: "26px 30px", position: "relative" }}>
      <FlashKeyframes />
      {/* Title and market picker share one line and one centre line. With the
          title's own eyebrow removed, flex-start left the picker floating above
          it on its own row. */}
      <section className="Page-Heading" style={{ marginBottom: 6 }}>
        <h1 style={{ margin: 0 }}>Playbook</h1>
      </section>
      <p style={{ margin: "0 0 16px", ...muted, fontSize: 14 }}>
        How a deal is read and moved — who it is for, what hurts, what to say, and what must be true
        at every stage before it goes forward.
      </p>

      {error && (
        <div className="Login-Error">
          {error}{" "}
          <button type="button" className="Soft-Button Small" onClick={() => setLoadTry((n) => n + 1)}>Try again</button>
        </div>
      )}
      {!businesses && !error && <p style={muted}>Loading…</p>}

      {businesses && businesses.length > 0 && (
        <>
          {/* Which playbook, and which size within it — one row, stuck together
              on purpose. They used to sit on two separate lines, which made
              neither easy to keep on screen while reading; merged so there is
              only one sticky offset to manage instead of two.
              top: 0, not a guessed offset. The 88px borrowed from other
              screens assumed this page is wrapped by this app's own top bar at
              a known height — wrong for however this page is actually being
              viewed, and it produced a real gap where card content visibly
              slid up underneath the bar instead of staying pinned. Zero is the
              one value that needs no assumption about anything outside this
              page: the bar sticks flush to whatever is actually the top. */}
          <div style={{
            position: "sticky", top: 0, zIndex: 5,
            background: "var(--surface)", borderBottom: "1px solid var(--c-200)",
            margin: "0 -30px 16px", padding: "10px 30px",
            display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap",
          }}>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {businesses.map((b) => (
                <button
                  key={b.slug}
                  onClick={() => chooseBiz(b.slug)}
                  style={{
                    display: "flex", alignItems: "center", gap: 6, padding: "6px 12px", borderRadius: 999,
                    border: "1px solid var(--c-200)", cursor: "pointer", fontSize: 12.5, fontWeight: 600,
                    background: activeBiz === b.slug ? "var(--blue-600)" : "var(--surface)",
                    color: activeBiz === b.slug ? "#fff" : "var(--c-700)",
                  }}
                >
                  <BookOpen size={13} /> {b.name}
                </button>
              ))}
            </div>
            {/* Each playbook supplies its own band table. Falls back to deal
                value for anything that has not written one. */}
            <BandPicker value={band} onChange={setBand} labels={current?.brief?.band_labels} />
          </div>

          {current && (
            <ExpandAllContext.Provider value={isSuper}>
            <PlaybookUIContext.Provider value={uiContextValue}>
            <GlossaryContext.Provider value={glossary}>
              {/* A real second column, not an overlay — the rail sits beside
                  the content and takes part in the page's own layout, the same
                  way it does in the reference. That is also what lets it use
                  `position: sticky` instead of `fixed`: sticky only works
                  relative to a place the element actually holds in the page. */}
              <div style={{ display: "flex", gap: 28, alignItems: "flex-start" }}>
                <div ref={contentRef} style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 14 }}>
                  {current.tagline && <p style={{ margin: 0, fontSize: 13, fontStyle: "italic", ...muted }}>{current.tagline}</p>}

                  {/* The brief first, then the mindset, then the stages — the
                      order a seller needs them in. The stages describe how a deal
                      gains momentum and take for granted that you already know
                      who you are talking to and what you are going to say. */}
                  <BriefSection brief={current.brief} band={band} stageCount={(current.stages || []).length} />

                  {/* Counted from the data. The heading said "eleven" for weeks
                      after Post-Sale took it to twelve, because the number was
                      typed into the copy. */}
                  {(current.stages || []).length > 0 && (
                    <SectionHeading
                      title={`The ${(current.stages || []).length} stages`}
                      note={`${(current.stages || []).length} gates. At each one, something must be true before the deal moves.`}
                    />
                  )}
                  {(current.stages || []).length === 0 && (
                    <p style={muted}>No stages recorded yet for {current.name}.</p>
                  )}
                  {(current.stages || [])
                    .slice()
                    .sort((a, b) => a.stage_number - b.stage_number)
                    .map((stage, i, all) => (
                      <StageCard key={stage.id} stage={stage} band={band} isLast={i === all.length - 1} />
                    ))}
                </div>
                <SectionNav
                  containerRef={contentRef}
                  watch={`${activeBiz}:${band}`}
                  band={band}
                  bizKey={activeBiz}
                  restoreId={sectionByBizRef.current[activeBiz]}
                  onActiveChange={onRailActiveChange}
                />
              </div>
            </GlossaryContext.Provider>
            </PlaybookUIContext.Provider>
            </ExpandAllContext.Provider>
          )}
        </>
      )}

      {businesses && businesses.length === 0 && (
        <p style={muted}>No playbook content yet.</p>
      )}
    </section>
  );
}
